import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { SEO } from "@/components/SEO";

const PrivacyPolicy = () => {
  return (
    <div className="min-h-screen bg-background">
      <SEO 
        title="Privacy Policy | EvGateway"
        description="EvGateway's Privacy Policy explains how we collect, use, disclose, and safeguard your personal information."
      />
      <Navbar />
      
      <main className="pt-24 pb-16">
        <div className="container mx-auto px-4 max-w-4xl">
          <h1 className="text-3xl md:text-4xl font-bold text-primary mb-2">EvGateway Privacy Policy</h1>
          
          <p className="text-lg font-medium text-foreground mb-6">
            Effective Date: <span className="font-bold">Jan 13th 2026</span>
          </p>
          
          <div className="prose prose-lg dark:prose-invert max-w-none space-y-6 text-muted-foreground">
            <p className="leading-relaxed">
              EvGateway ("EvGateway," "we," "us," or "our") values your privacy and is committed to protecting personal information. This Privacy Policy explains how we collect, use, disclose, and safeguard information when you access or use our websites, mobile applications, EV charging network services, fleet and enterprise solutions, customer support, and related products and services (collectively, the "Services").
            </p>

            <p className="leading-relaxed">
              This Privacy Policy is designed to comply with applicable privacy laws, including GDPR, UK GDPR, CCPA/CPRA, and other applicable U.S. and international privacy regulations.
            </p>

            <section>
              <h2 className="text-xl font-bold text-foreground mt-8 mb-3">1. Scope</h2>
              <p>
                This Privacy Policy applies when you visit EvGateway websites, use our mobile apps, interact with EV charging stations managed by EvGateway, participate in fleet or enterprise programs, use roaming services, or communicate with EvGateway.
              </p>
              <p className="mt-3">
                In some cases, EvGateway acts as a service provider or data processor on behalf of charging station owners, fleet operators, utilities, or enterprise customers.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-bold text-foreground mt-8 mb-3">2. Information We Collect</h2>
              <p>
                We may collect identifiers and contact information, account and profile details, charging session data, location data (with consent), device and technical information, payment transaction metadata, communications, cookies, and information provided about others.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-bold text-foreground mt-8 mb-3">3. Sources of Information</h2>
              <p>
                Information may be collected directly from users, automatically from devices, from charging infrastructure, business customers, and service providers.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-bold text-foreground mt-8 mb-3">4. How We Use Information</h2>
              <p>
                We use information to provide and operate services, enable charging, process payments, provide customer support, ensure security, comply with legal obligations, improve services, and conduct analytics.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-bold text-foreground mt-8 mb-3">5. Legal Bases for Processing</h2>
              <p>
                Processing is based on contract performance, legitimate interests, consent, and legal obligations, where applicable.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-bold text-foreground mt-8 mb-3">6. How We Share Information</h2>
              <p>
                We may share information with service providers, business customers, roaming partners, legal authorities, or in connection with business transfers. EvGateway does not sell personal information.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-bold text-foreground mt-8 mb-3">7. Cookies and Similar Technologies</h2>
              <p>
                Cookies are used for essential functionality, preferences, analytics, and optional marketing. Users can control cookies via browser settings.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-bold text-foreground mt-8 mb-3">8. Data Retention</h2>
              <p>
                Data is retained only as long as necessary. Typical retention includes account data during account life, transaction records for 3–7 years, and logs for security and compliance.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-bold text-foreground mt-8 mb-3">9. Security</h2>
              <p>
                We use administrative, technical, and physical safeguards to protect personal information.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-bold text-foreground mt-8 mb-3">10. International Data Transfers</h2>
              <p>
                Information may be processed outside your country with appropriate safeguards.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-bold text-foreground mt-8 mb-3">11. Your Privacy Rights</h2>
              <p>
                Depending on location, users may access, correct, delete, port, or restrict processing of their data, and withdraw consent.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-bold text-foreground mt-8 mb-3">12. Children's Privacy</h2>
              <p>
                Services are not intended for children under 16.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-bold text-foreground mt-8 mb-3">13. Third-Party Links</h2>
              <p>
                EvGateway is not responsible for third-party privacy practices.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-bold text-foreground mt-8 mb-3">14. Changes to This Policy</h2>
              <p>
                Updates will be posted with a revised effective date.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-bold text-foreground mt-8 mb-3">15. Contact Us</h2>
              <p className="font-medium text-foreground">EvGateway Privacy Team</p>
              <p>
                Email: <a href="mailto:privacy@evgateway.com" className="text-primary hover:underline">privacy@evgateway.com</a>
              </p>
              <p>
                Address: 19681 Da Vinci, Foothill Ranch, CA 92610
              </p>
            </section>

            <section>
              <h2 className="text-xl font-bold text-foreground mt-8 mb-3">16. California and U.S. State Privacy Notice</h2>
              <p>
                Residents may exercise rights to access, delete, correct, and opt out of certain data sharing without discrimination.
              </p>
            </section>
          </div>
        </div>
      </main>
      
      <Footer />
    </div>
  );
};

export default PrivacyPolicy;
